import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  parseImageRef,
  type ClientInstance,
  type InstanceActionAudit,
  type InstanceActionResult,
  type InstanceActionType,
  type InstanceDigestOption,
  RECREATE_RECOVERIES,
  type RecreateRecovery,
  type ReassignWarning,
  type RegistryTag,
} from "../infrastructure.types";
import { canManageInfra } from "../rbac";
import { runInstanceAction } from "../instanceActions";
import { subscribeInstanceActionProgress } from "../instanceStreams";
import { env } from "config/environment";
import { newUuid } from "shared/newUuid";
import SelfUpdateWatch from "./SelfUpdateWatch";
import ActionProgressWatch from "./progress/ActionProgressWatch";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
/**
 * A Feathers `find` response: either a paginated envelope
 * (`{ data: [...] }`, what KnexServices return) or the bare array a custom
 * non-paginated service returns. The audit reads below use the raw response
 * snapshot (not the shared entity cache), so normalise both shapes here.
 */
type FindResponse<T> = T[] | { data?: T[] } | null | undefined;

/** Normalise a raw Feathers `find` response into a plain array of rows. */
function rowsOf<T>(result: FindResponse<T>): T[] {
  if (Array.isArray(result)) return result;
  const data = result?.data;
  return Array.isArray(data) ? data : [];
}

/**
 * How many audit rows loadAudit pulls per instance ($limit). When the loaded
 * set hits this cap the history may be truncated, so the view shows a
 * "showing the latest N" caption (see {@link InstanceActionsStore.auditAtCap}).
 */
export const AUDIT_HISTORY_LIMIT = 100;

/**
 * How many audit rows loadRollbackOptions scans to build the prior-digest list
 * ($limit). Rollback targets are DERIVED from that scan, so this is a cap on
 * how far back a rollback can reach — an instance that has been deployed more
 * than this many times cannot be rolled back to a digest older than the window,
 * and the picker gives no hint of it. {@link InstanceActionsStore
 * .rollbackOptionsAtCap} makes the dialog say so.
 */
export const ROLLBACK_SCAN_LIMIT = 200;

/** The slice of the global ClientStore the move picker reads. */
interface GlobalClientStore {
  clients?: Array<{ id: string; name?: string; deletedAt?: string | null }>;
  fetchClients?: () => Promise<void>;
}

/** What a Move-to-client attempt did — shown by the opener after the dialog. */
export interface ReassignOutcome {
  ok: boolean;
  instanceId: string;
  containerName: string;
  fromName: string;
  toName: string;
  /** Env keys still pointing at the previous client (values never sent). */
  warnings: ReassignWarning[];
  error: string | null;
  /** On failure: what the API says became of the ORIGINAL container, or null
   *  when it did not say (a transport error or timeout — the move may still
   *  have run, so nothing may be claimed about it). */
  recovery: RecreateRecovery | null;
  /** On success: Docker's word for the moved container (`running`,
   *  `created`, `unknown`), or null when the API did not say. */
  containerState: string | null;
  /** On success: `starting` when the moved container runs but its health
   *  check had not passed when HQ stopped watching, `unknown` when it has a
   *  health check but Docker reported no health status; null otherwise. */
  containerHealth: string | null;
  /** On success: false when HQ's instance record was not updated. */
  rowUpdated: boolean;
}

/** The recovery outcome the API put on a failed action's error, if any. */
export function recoveryOf(err: unknown): RecreateRecovery | null {
  const value = (err as { data?: { recovery?: unknown } } | null)?.data?.recovery;
  return typeof value === "string" && (RECREATE_RECOVERIES as ReadonlyArray<string>).includes(value)
    ? (value as RecreateRecovery)
    : null;
}

/**
 * Hand a container-action result to the shared self-update watch. Every view
 * that can act on the container serving HQ calls this after its action, so
 * the banner follows the update wherever it was started from.
 */
export function followHandOff(
  rootStore: RootStore,
  result: InstanceActionResult | undefined,
  instance: ClientInstance | null
): void {
  (rootStore.instanceActionsViewStore as InstanceActionsStore | undefined)?.watchIfHandedOff(result, instance);
}

/** GUID equality that ignores case (MSSQL answers UNIQUEIDENTIFIER upper-case). */
const sameId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/**
 * Cross-view store backing the Deploy / Rollback / confirm dialogs and the
 * instance action-audit history. It is intentionally view-agnostic: the
 * ClientInstances grid, the ClientDetail Instances tab, and the InstanceDetail
 * view all open the SAME dialogs through this one store, so deploy/rollback
 * behave identically everywhere and there is one place that loads the audit
 * trail / prior digests.
 *
 * Plain class with `makeAutoObservable` (route ViewStores never extend
 * ViewStore). Backed by the registered `instance-actions` (mutating action seam)
 * and `instance-action-audit` (read-only history) ServiceStores. No secret is
 * ever read or surfaced — `instance-action-audit` rows carry ids/actions/digests
 * only.
 *
 * RBAC: deploy/rollback are operator+; the API enforces it and this store gates
 * via {@link canManageInfra}. Confirmation for stop/restart is handled here too
 * so destructive lifecycle actions get a guard regardless of entry point.
 */
class InstanceActionsStore {
  rootStore: RootStore;

  // ---- Active target instance (whichever view opened a dialog) ----
  target: ClientInstance | null = null;

  // ---- Deploy dialog form ----
  deployImage = "";
  deployTag = "";
  deploying = false;
  deployError: string | null = null;
  /** Tags available in the registry for the deploy repository (the picker feed). */
  deployTags: RegistryTag[] = [];
  deployTagsLoading = false;
  /** True when the deploy tags could not be loaded (registry unreachable / none). */
  deployTagsUnavailable = false;

  // ---- One-click update-to-latest ----
  updating = false;
  updateError: string | null = null;

  // ---- Rollback dialog ----
  rollbackOptions: InstanceDigestOption[] = [];
  rollbackOptionsLoading = false;
  /** True when the audit scan that produced {@link rollbackOptions} came back
   *  full — see {@link ROLLBACK_SCAN_LIMIT}. Surfaced by the Rollback dialog so
   *  "these are the digests you can roll back to" is not read as "these are the
   *  digests this instance has ever run". */
  rollbackOptionsAtCap = false;
  selectedRollbackDigest: string | null = null;
  rollingBack = false;
  /** A failed rollback submit. */
  rollbackError: string | null = null;
  /** The prior-digest scan did not load — the dialog says so instead of
   *  "nothing to roll back to yet". */
  rollbackOptionsError: string | null = null;

  // ---- Move to client (reassign) dialog ----
  /** The client the container is being moved TO (the picker's value). */
  reassignClientId = "";
  reassigning = false;
  reassignError: string | null = null;
  /**
   * What the last move did, kept after the dialog closes (the dialog framework
   * closes on submit either way) so the opener can show it prominently:
   * from → to, and the env keys that still point at the previous client.
   */
  reassignOutcome: ReassignOutcome | null = null;

  // ---- Confirm (stop/restart) dialog ----
  confirmAction: InstanceActionType | null = null;
  confirming = false;

  // ---- Audit / deploy history (per instance) ----
  audit: InstanceActionAudit[] = [];
  auditLoading = false;
  /** The history did not load — not "No actions recorded for this instance". */
  auditError: string | null = null;
  auditInstanceId: string | null = null;
  /** Action filter over the loaded audit rows ("all" = no filter). */
  auditActionFilter = "all";
  /** Status filter over the loaded audit rows ("all" = no filter). */
  auditStatusFilter = "all";

  /** Follows an update/rollback/restart of the container serving HQ, which
   *  the API hands to a helper container (see SelfUpdateWatch). */
  selfUpdate: SelfUpdateWatch;

  /** The steps of the action in flight, for the dialog that sent it (see
   *  progress/ActionProgressWatch). Active only while a call is out. */
  actionProgress = new ActionProgressWatch();
  /** Every live progress subscription, so a sign-out can drop them all. */
  private readonly progressDisposers = new Set<() => void>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.selfUpdate = new SelfUpdateWatch({
      fetchVersion: async () => {
        const res = await fetch(`${env.apiUrl}/health`, { cache: "no-store" });
        if (!res.ok) return null;
        const body = (await res.json()) as { version?: unknown };
        return typeof body?.version === "string" ? body.version : null;
      },
      findAudit: async (instanceId, handoffId) => {
        const result = await this.auditService?.find({
          query: { instanceId, handoffId, $limit: 20, $sort: { createdAt: -1 } },
        });
        return rowsOf<InstanceActionAudit>(result as unknown as FindResponse<InstanceActionAudit>);
      },
    });
    // progressDisposers stays a plain Set: an observable Set does not hold a
    // function value by identity (add then has → false), so it could never
    // release what it holds.
    makeAutoObservable<InstanceActionsStore, "progressDisposers">(this, {
      rootStore: false,
      selfUpdate: false,
      actionProgress: false,
      progressDisposers: false,
    });
  }

  /**
   * Run one action with {@link actionProgress} following it. A correlation
   * token is minted and handed to `call`, which MUST send it as the request's
   * `progressToken`: the API echoes it on every event of that request's run,
   * and the watch follows only those — never another operator's action on
   * the same container. The subscription is made BEFORE the call goes out
   * (the first step can be emitted as soon as the API has the request) and
   * torn down after it answers either way.
   *
   * A call made while this instance's action is already being followed
   * (updateToLatest inside runConfirmed) runs inside the outer watch with
   * its token. A call on ANOTHER instance while one is in flight takes the
   * watch over; the earlier call's teardown then leaves it alone — the token
   * is the ownership mark, and a sign-out (reset) clears it the same way.
   */
  private async watchProgress<T>(
    instance: ClientInstance,
    action: InstanceActionType,
    call: (progressToken: string) => Promise<T>
  ): Promise<T> {
    const watch = this.actionProgress;
    if (watch.active && watch.token && sameId(watch.instanceId, instance.id)) {
      return call(watch.token);
    }
    const token = newUuid();
    watch.begin(instance.id, action, token);
    let dispose: (() => void) | null = null;
    try {
      // apply() takes only events echoing the watch's CURRENT token, so a
      // subscription outliving its call can never feed a later watch.
      dispose = await subscribeInstanceActionProgress(this.rootStore, (event) => {
        watch.apply(event);
      });
      if (dispose) this.progressDisposers.add(dispose);
    } catch (err: unknown) {
      // No live progress, then: the dialog says it has heard nothing.
      console.error("Could not subscribe to action progress:", err);
    }
    try {
      return await call(token);
    } finally {
      // Still ours to release? A sign-out (endProgressWatch) may have done it.
      if (dispose && this.progressDisposers.delete(dispose)) dispose();
      if (watch.token === token) watch.end();
    }
  }

  /**
   * Run an action ANOTHER store drives — the Environment card's apply
   * (InstanceEnvStore) — under this store's progress watch, so the one shared
   * ActionProgressPanel (which reads {@link actionProgress} and names
   * {@link target}) follows it exactly as it follows an update.
   */
  runWithProgress<T>(
    instance: ClientInstance,
    action: InstanceActionType,
    call: (progressToken: string) => Promise<T>
  ): Promise<T> {
    this.target = instance;
    return this.watchProgress(instance, action, call);
  }

  /** Test seam for {@link watchProgress} (which is private by design). */
  watchProgressForTest<T>(
    instance: ClientInstance,
    action: InstanceActionType,
    call: (progressToken: string) => Promise<T>
  ): Promise<T> {
    return this.watchProgress(instance, action, call);
  }

  /** Drop every live progress subscription and the watch (sign-out). */
  private endProgressWatch(): void {
    for (const dispose of this.progressDisposers) {
      if (typeof dispose === "function") dispose();
    }
    this.progressDisposers.clear();
    this.actionProgress.end();
  }

  /** A hand-off (the container serving HQ) is followed, not reported done. */
  watchIfHandedOff(result: InstanceActionResult | undefined, instance: ClientInstance | null): void {
    if (!result?.handedOff) return;
    this.selfUpdate.begin(result, instance?.containerName || instance?.id || "HQ");
  }

  /** The registered `instance-actions` ServiceStore (the mutating action seam). */
  private get actionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("instance-actions") ?? this.rootStore.instanceActionsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered read-only `instance-action-audit` ServiceStore. */
  private get auditService(): ServiceStoreType | null {
    return (
      (this.rootStore.instanceActionAuditStore as ServiceStoreType | null) ??
      null
    );
  }

  /** The registered read-through `registry-tags` ServiceStore (deploy picker). */
  private get registryTagsService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore("registry-tags") as unknown as ServiceStoreType | null) ??
      null
    );
  }

  // ---- RBAC ----

  get canManage(): boolean {
    return canManageInfra(this.rootStore);
  }

  // ---- Target ----

  setTarget(instance: ClientInstance | null) {
    this.target = instance;
  }

  // ---- Deploy dialog ----

  /** Open the deploy dialog for an instance, seeding the form from its image. */
  startDeploy(instance: ClientInstance) {
    this.target = instance;
    this.deployError = null;
    // Seed image (repo, no tag) + tag from the running image reference when
    // possible so the operator only changes the tag in the common case. THE
    // shared parser handles registry ports AND digest-pinned refs — the old
    // inline split turned `repo@sha256:abc…` into image "repo@sha256" + tag
    // "abc…", which is what a container deployed by the digest-pinning era of
    // the deploy action carries.
    const parsed = parseImageRef(instance.imageRef);
    this.deployImage = [parsed.registry, parsed.repository]
      .filter(Boolean)
      .join("/");
    this.deployTag = parsed.tag ?? "";
    // Load the registry tag catalog for the picker (best-effort). Prefer the
    // latest-known tag from the sweep if the instance already flags an update.
    this.deployTags = [];
    this.deployTagsUnavailable = false;
    void this.loadDeployTags(this.deployImage);
  }

  setDeployImage(value: string) {
    this.deployImage = value;
    // Repository changed → refresh the tag list for the new repo.
    void this.loadDeployTags(value);
  }

  setDeployTag(value: string) {
    this.deployTag = value;
  }

  /**
   * Load available tags for a repository from the read-through `registry-tags`
   * service (newest first). Best-effort: on failure the dialog falls back to the
   * free-text tag field (deployTagsUnavailable = true).
   */
  async loadDeployTags(repository: string) {
    const repo = (repository || "").trim();
    if (!repo) {
      runInAction(() => {
        this.deployTags = [];
      });
      return;
    }
    const ended = sessionMark(this.rootStore);
    this.deployTagsLoading = true;
    this.deployTagsUnavailable = false;
    try {
      const result = await this.registryTagsService?.find({
        query: { repository: repo },
      });
      if (ended()) return;
      const rows = rowsOf<RegistryTag>(
        result as unknown as FindResponse<RegistryTag>
      );
      runInAction(() => {
        this.deployTags = rows;
        this.deployTagsUnavailable = rows.length === 0;
      });
    } catch (err: unknown) {
      if (ended()) return;
      console.error("Failed to load registry tags:", err);
      runInAction(() => {
        this.deployTags = [];
        this.deployTagsUnavailable = true;
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.deployTagsLoading = false;
        });
      }
    }
  }

  /** The digest currently running (for the "currently running" picker marker). */
  get runningDigest(): string | null {
    return this.target?.currentDigest ?? null;
  }

  get isDeployValid(): boolean {
    return this.deployImage.trim().length > 0 && this.deployTag.trim().length > 0;
  }

  /**
   * Submit the deploy. operator+. Returns true on success so the dialog can
   * close and the opener can refresh. Re-pulls audit so the history reflects it.
   */
  async submitDeploy(): Promise<boolean> {
    if (!this.target || !this.canManage || !this.isDeployValid) return false;
    const instanceId = this.target.id;
    const target = this.target;
    this.deploying = true;
    this.deployError = null;
    try {
      const ended = sessionMark(this.rootStore);
      const result = await this.watchProgress(target, "deploy", (progressToken) =>
        runInstanceAction(this.actionsService, {
          action: "deploy",
          instanceId,
          image: this.deployImage.trim(),
          tag: this.deployTag.trim(),
          progressToken,
        })
      );
      if (ended()) return false;
      this.watchIfHandedOff(result, target);
      // Refresh the deploy history if we're showing this instance's audit.
      if (this.auditInstanceId === instanceId) await this.loadAudit(instanceId);
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.deployError =
            err instanceof Error ? err.message : "Failed to deploy";
        }
      });
      console.error("Deploy failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.deploying = false;
      });
    }
  }

  // ---- One-click update-to-latest ----

  /**
   * Update an instance to the newest registry image in one call. The API
   * re-resolves the latest tag per the instance's track policy (a fresh ACR
   * check), refuses when already up to date, and runs the guarded deploy
   * pipeline. operator+. Returns true on success.
   */
  async updateToLatest(instance: ClientInstance): Promise<boolean> {
    if (!this.canManage) return false;
    this.updating = true;
    this.updateError = null;
    try {
      const ended = sessionMark(this.rootStore);
      const result = await this.watchProgress(instance, "update-to-latest", (progressToken) =>
        runInstanceAction(this.actionsService, {
          action: "update-to-latest",
          instanceId: instance.id,
          progressToken,
        })
      );
      if (ended()) return false;
      this.watchIfHandedOff(result, instance);
      if (this.auditInstanceId === instance.id) await this.loadAudit(instance.id);
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.updateError =
            err instanceof Error ? err.message : "Failed to update instance";
        }
      });
      console.error("Update-to-latest failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.updating = false;
      });
    }
  }

  /**
   * Update every instance for a client to the newest image (the batch form).
   * The API updates api then web sequentially, skipping already-current ones.
   */
  async updateClientToLatest(clientId: string): Promise<boolean> {
    if (!this.canManage) return false;
    this.updating = true;
    this.updateError = null;
    try {
      const result = await runInstanceAction(this.actionsService, {
        action: "update-to-latest",
        clientId,
      });
      this.watchIfHandedOff(result, null);
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.updateError =
            err instanceof Error ? err.message : "Failed to update client instances";
        }
      });
      console.error("Update-client-to-latest failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.updating = false;
      });
    }
  }

  // ---- Rollback dialog ----

  /**
   * Open the rollback dialog for an instance: load its prior digests from the
   * action-audit history (most recent deploys first), excluding the digest it
   * is currently running.
   */
  async startRollback(instance: ClientInstance) {
    this.target = instance;
    this.rollbackError = null;
    this.rollbackOptionsError = null;
    this.selectedRollbackDigest = null;
    this.rollbackOptions = [];
    this.rollbackOptionsAtCap = false;
    await this.loadRollbackOptions(instance);
  }

  /** Re-run the prior-digest scan for the open Rollback dialog. */
  async retryRollbackOptions() {
    if (this.target) await this.loadRollbackOptions(this.target);
  }

  private async loadRollbackOptions(instance: ClientInstance) {
    this.rollbackOptionsLoading = true;
    this.rollbackOptionsError = null;
    try {
      // Read the find RESPONSE directly (not the shared entity cache): this view
      // also loads the audit list with a different query, so decouple the two.
      // findOrThrow: a swallowed failure resolved null and read as "No prior
      // image digests are recorded" — a false "nothing to roll back to".
      const result = await findOrThrow(this.auditService, {
        query: {
          instanceId: instance.id,
          $limit: ROLLBACK_SCAN_LIMIT,
          $sort: { createdAt: -1 },
        },
      });
      // Remember whether the scan came back full — the digest list is derived
      // from these rows, so a full scan means older digests may exist that the
      // picker cannot offer. Recorded BEFORE the rows are collapsed to distinct
      // digests, since that collapse destroys the evidence.
      const scannedAtCap =
        rowsOf<InstanceActionAudit>(
          result as unknown as FindResponse<InstanceActionAudit>
        ).length >= ROLLBACK_SCAN_LIMIT;
      const rows = rowsOf<InstanceActionAudit>(
        result as unknown as FindResponse<InstanceActionAudit>
      );
      // Build the distinct list of digests this instance has run, newest first,
      // skipping the digest it's currently on (rolling back to "now" is a no-op).
      const current = instance.currentDigest || null;
      const seen = new Set<string>();
      const options: InstanceDigestOption[] = [];
      for (const row of rows) {
        // Only SUCCESSFUL deploy/rollback rows are valid sources — never failed
        // attempts or compensation rows (the API's assertAuditedDigest applies
        // the same filter, so anything else would be rejected anyway).
        if (row.status !== "success") continue;
        if (row.action !== "deploy" && row.action !== "rollback") continue;
        // Only offer `toDigest` candidates: the API's assertAuditedDigest
        // accepts a digest only when it appears as a `toDigest` on an audited
        // deploy/rollback row, so a `fromDigest`-only digest (the image the
        // container ran BEFORE its first audited deploy) would always be
        // rejected with BadRequest.
        const candidate = row.toDigest;
        if (!candidate) continue;
        if (candidate === current) continue;
        if (seen.has(candidate)) continue;
        seen.add(candidate);
        options.push({
          digest: candidate,
          image: row.imageRef ?? null,
          deployedAt: row.createdAt ?? null,
        });
      }
      runInAction(() => {
        this.rollbackOptions = options;
        this.rollbackOptionsAtCap = scannedAtCap;
        // Pre-select the most recent prior digest for convenience.
        this.selectedRollbackDigest = options[0]?.digest ?? null;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.rollbackOptionsError = loadErrorMessage(
            err,
            "Failed to load prior image digests"
          );
        }
      });
      console.error("Failed to load rollback options:", err);
    } finally {
      runInAction(() => {
        this.rollbackOptionsLoading = false;
      });
    }
  }

  setSelectedRollbackDigest(digest: string) {
    this.selectedRollbackDigest = digest;
  }

  get isRollbackValid(): boolean {
    return !!this.selectedRollbackDigest;
  }

  /** Submit the rollback. operator+. */
  async submitRollback(): Promise<boolean> {
    if (
      !this.target ||
      !this.canManage ||
      !this.selectedRollbackDigest
    ) {
      return false;
    }
    const instanceId = this.target.id;
    const target = this.target;
    this.rollingBack = true;
    this.rollbackError = null;
    try {
      const ended = sessionMark(this.rootStore);
      const result = await this.watchProgress(target, "rollback", (progressToken) =>
        runInstanceAction(this.actionsService, {
          action: "rollback",
          instanceId,
          toDigest: this.selectedRollbackDigest as string,
          progressToken,
        })
      );
      if (ended()) return false;
      this.watchIfHandedOff(result, target);
      if (this.auditInstanceId === instanceId) await this.loadAudit(instanceId);
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.rollbackError =
            err instanceof Error ? err.message : "Failed to roll back";
        }
      });
      console.error("Rollback failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.rollingBack = false;
      });
    }
  }

  // ---- Move to client (reassign) ----

  /** The global ClientStore (the full client list) — the move picker's feed. */
  private get globalClientStore(): GlobalClientStore | undefined {
    return (this.rootStore as unknown as Record<string, unknown>).clientStore as
      | GlobalClientStore
      | undefined;
  }

  /**
   * Open the Move-to-client dialog for an instance. Seeds the client list if the
   * global cache is cold, and clears any previous outcome banner.
   */
  startReassign(instance: ClientInstance) {
    const clients = this.globalClientStore;
    if (clients && !clients.clients?.length) void clients.fetchClients?.();
    this.target = instance;
    this.reassignClientId = "";
    this.reassignError = null;
    this.reassignOutcome = null;
  }

  setReassignClientId(clientId: string) {
    this.reassignClientId = clientId;
  }

  /** Display name of a client id, from the global list (falls back to the id). */
  clientNameOf(clientId: string | null | undefined): string {
    if (!clientId) return "no client";
    const hit = this.globalClientStore?.clients?.find((c) => sameId(c.id, clientId));
    return hit?.name || clientId;
  }

  /** The client the target belongs to now (what the dialog calls "current"). */
  get reassignFromName(): string {
    const t = this.target;
    if (!t?.clientId) return "no client";
    return t.clientName || this.clientNameOf(t.clientId);
  }

  get reassignToName(): string {
    return this.reassignClientId ? this.clientNameOf(this.reassignClientId) : "";
  }

  /**
   * Where the container can be moved: every non-archived client except the
   * one it already belongs to (the API refuses that move too).
   */
  get reassignClientOptions(): Array<{ id: string; name: string }> {
    const current = this.target?.clientId ?? null;
    return (this.globalClientStore?.clients ?? [])
      .filter((c) => !c.deletedAt && !sameId(c.id, current))
      .map((c) => ({ id: c.id, name: c.name || c.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get isReassignValid(): boolean {
    const t = this.target;
    if (!t?.id || !this.reassignClientId) return false;
    if (sameId(t.clientId, this.reassignClientId)) return false;
    // A missing row has no container to recreate; the API refuses it too.
    return t.status !== "missing";
  }

  /**
   * Move the target container to the chosen client. operator+. The API
   * recreates the container (so it RESTARTS) with the new client label and
   * answers with any env keys still pointing at the previous client, which are
   * kept on {@link reassignOutcome} for the opener to show. Returns true on
   * success.
   */
  async submitReassign(): Promise<boolean> {
    const t = this.target;
    if (!t || !this.canManage || !this.isReassignValid) return false;
    const instanceId = t.id;
    const toClientId = this.reassignClientId;
    const fromName = this.reassignFromName;
    const toName = this.reassignToName;
    const containerName = t.containerName || t.id;
    this.reassigning = true;
    this.reassignError = null;
    this.reassignOutcome = null;
    try {
      const ended = sessionMark(this.rootStore);
      const result = await this.watchProgress(t, "reassign", (progressToken) =>
        runInstanceAction(this.actionsService, {
          action: "reassign",
          instanceId,
          clientId: toClientId,
          progressToken,
        })
      );
      if (ended()) return false;
      runInAction(() => {
        this.reassignOutcome = {
          ok: true,
          instanceId,
          containerName,
          fromName,
          toName,
          warnings: Array.isArray(result?.warnings) ? result.warnings : [],
          error: null,
          recovery: null,
          containerState: typeof result?.containerState === "string" ? result.containerState : null,
          containerHealth: typeof result?.containerHealth === "string" ? result.containerHealth : null,
          rowUpdated: result?.rowUpdated !== false,
        };
        this.reassignClientId = "";
      });
      if (this.auditInstanceId === instanceId) await this.loadAudit(instanceId);
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to move the instance";
      runInAction(() => {
        if (!isSessionEnded(err)) this.reassignError = message;
        if (!isSessionEnded(err)) {
          this.reassignOutcome = {
            ok: false,
            instanceId,
            containerName,
            fromName,
            toName,
            warnings: [],
            error: message,
            recovery: recoveryOf(err),
            containerState: null,
            containerHealth: null,
            rowUpdated: false,
          };
        }
      });
      console.error("Reassign failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.reassigning = false;
      });
    }
  }

  /** Dismiss the outcome banner. */
  clearReassignOutcome() {
    this.reassignOutcome = null;
  }

  // ---- Confirm (stop/restart) dialog ----

  /** Open the confirm dialog for a destructive lifecycle action. */
  startConfirm(instance: ClientInstance, action: InstanceActionType) {
    this.target = instance;
    this.confirmAction = action;
  }

  clearConfirm() {
    this.confirmAction = null;
    this.confirming = false;
  }

  /**
   * Run the confirmed lifecycle action. operator+. Returns true on success.
   * NOTE: the actual optimistic status + grid refresh lives in the opener store
   * (ClientInstancesStore / ClientDetailStore), so this returns the chosen
   * instance+action to the caller via the resolved boolean and the opener
   * re-invokes its own `runLifecycleAction`. To keep a single transport, the
   * caller passes a runner.
   */
  async runConfirmed(
    runner: (
      instance: ClientInstance,
      action: InstanceActionType,
      /** Send this as the request's `progressToken` so the dialog's progress
       *  panel follows the action (see watchProgress). */
      progressToken: string
    ) => Promise<boolean>
  ): Promise<boolean> {
    if (!this.target || !this.confirmAction || !this.canManage) return false;
    const target = this.target;
    const action = this.confirmAction;
    this.confirming = true;
    try {
      return await this.watchProgress(target, action, (progressToken) => runner(target, action, progressToken));
    } finally {
      runInAction(() => {
        this.clearConfirm();
      });
    }
  }

  // ---- Audit / deploy history ----

  /** Distinct actions present in the loaded audit rows — the Action filter's
   *  options (derived, so the picker only offers values that exist). */
  get auditActionOptions(): string[] {
    return [...new Set(this.audit.map((r) => r.action).filter(Boolean))].sort();
  }

  /** Distinct statuses present in the loaded audit rows — the Status filter's
   *  options. */
  get auditStatusOptions(): string[] {
    return [
      ...new Set(this.audit.map((r) => r.status).filter((s): s is string => !!s)),
    ].sort();
  }

  /** The audit rows under the active Action/Status filters (what the history
   *  table renders). */
  get filteredAudit(): InstanceActionAudit[] {
    let rows = this.audit;
    if (this.auditActionFilter !== "all") {
      rows = rows.filter((r) => r.action === this.auditActionFilter);
    }
    if (this.auditStatusFilter !== "all") {
      rows = rows.filter((r) => (r.status ?? "") === this.auditStatusFilter);
    }
    return rows;
  }

  /** True when the loaded rows hit loadAudit's server-side cap — older history
   *  may exist beyond what was fetched. */
  get auditAtCap(): boolean {
    return this.audit.length >= AUDIT_HISTORY_LIMIT;
  }

  setAuditActionFilter(value: string) {
    this.auditActionFilter = value;
  }

  setAuditStatusFilter(value: string) {
    this.auditStatusFilter = value;
  }

  /**
   * Load the append-only action audit for one instance (read-only). Mirrors the
   * grid's read gate (viewer+). No secret is present in these rows.
   */
  async loadAudit(instanceId: string) {
    if (this.auditInstanceId !== instanceId) {
      // A different instance's history — its filters don't carry over (a
      // refresh of the SAME instance keeps them).
      this.auditActionFilter = "all";
      this.auditStatusFilter = "all";
    }
    this.auditInstanceId = instanceId;
    this.auditLoading = true;
    this.auditError = null;
    try {
      // Read the find RESPONSE snapshot (not the shared entity cache) — see
      // loadRollbackOptions: the two reads use different queries on this service.
      // findOrThrow: a swallowed failure read as "No actions recorded".
      const result = await findOrThrow(this.auditService, {
        query: {
          instanceId,
          $limit: AUDIT_HISTORY_LIMIT,
          $sort: { createdAt: -1 },
        },
      });
      runInAction(() => {
        this.audit = rowsOf<InstanceActionAudit>(
          result as unknown as FindResponse<InstanceActionAudit>
        );
      });
    } catch (err: unknown) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      console.error("Failed to load instance action audit:", err);
      runInAction(() => {
        this.audit = [];
        this.auditError = loadErrorMessage(err, "Failed to load the action history");
      });
    } finally {
      runInAction(() => {
        this.auditLoading = false;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.endProgressWatch();
    this.target = null;
    this.deployImage = "";
    this.deployTag = "";
    this.deploying = false;
    this.deployError = null;
    this.deployTags = [];
    this.deployTagsLoading = false;
    this.deployTagsUnavailable = false;
    this.updating = false;
    this.updateError = null;
    this.rollbackOptions = [];
    this.rollbackOptionsLoading = false;
    this.rollbackOptionsAtCap = false;
    this.selectedRollbackDigest = null;
    this.rollingBack = false;
    this.rollbackError = null;
    this.rollbackOptionsError = null;
    this.reassignClientId = "";
    this.reassigning = false;
    this.reassignError = null;
    this.reassignOutcome = null;
    this.confirmAction = null;
    this.confirming = false;
    this.audit = [];
    this.auditLoading = false;
    this.auditError = null;
    this.auditInstanceId = null;
    this.auditActionFilter = "all";
    this.auditStatusFilter = "all";
    // The self-update watch polls /health and instance-action-audit every 3 s
    // and keeps a banner naming the container and action. The update itself
    // carries on server-side (a helper container does it); what stops here is
    // THIS tab following it for a person who has signed out — its polls would
    // otherwise run on, rejected, under the next person's session.
    this.selfUpdate.dismiss();
  }
}

export default InstanceActionsStore;
